export function normalizePartnerId(partnerId) {
  if (partnerId === null || partnerId === undefined || partnerId === '') return null;
  if (typeof partnerId === 'string') return partnerId;
  if (partnerId.$oid) return partnerId.$oid;
  return String(partnerId);
}

export function filterExceptionsByPartner(items, partnerId) {
  const norm = normalizePartnerId(partnerId);
  if (!norm) return items;
  return items.filter(item => normalizePartnerId(item.partnerId) === norm);
}

export function mergeExceptionSets(fetched, fallback) {
  const initial = fallback || { currency: [], gameType: [], table: [] };
  return {
    currency: fetched.currency.length ? fetched.currency : (initial.currency || []),
    gameType: fetched.gameType.length ? fetched.gameType : (initial.gameType || []),
    table: fetched.table.length ? fetched.table : (initial.table || []),
  };
}

export async function fetchCountryExceptions(http, countryCode, partnerId = null) {
  const request = (config) => http(config);
  const params = { countryCode };
  const [cur, gt, tbl] = await Promise.all([
    request({ url: '/api/v1/country-except', method: 'GET', params }),
    request({ url: '/api/v1/country-except-gametype', method: 'GET', params }),
    request({ url: '/api/v1/country-except-table-id', method: 'GET', params }),
  ]);

  const filter = (items) => (partnerId ? filterExceptionsByPartner(items, partnerId) : items);

  return {
    currency: filter(cur.data),
    gameType: filter(gt.data),
    table: filter(tbl.data),
  };
}

export function hasExceptionItems(data) {
  return (data?.currency?.length || 0) > 0
    || (data?.gameType?.length || 0) > 0
    || (data?.table?.length || 0) > 0;
}

export async function deleteExceptionItems(http, items) {
  const deletes = [
    ...(items?.currency || []).filter(i => i.id).map(i => ['country-except', i.id]),
    ...(items?.gameType || []).filter(i => i.id).map(i => ['country-except-gametype', i.id]),
    ...(items?.table || []).filter(i => i.id).map(i => ['country-except-table-id', i.id]),
  ];

  for (const [resource, id] of deletes) {
    await http({ url: `/api/v1/${resource}/${id}`, method: 'DELETE' });
  }
}

export async function isCountryBlocked(http, countryCode) {
  const code = (countryCode || '').trim().toUpperCase();
  if (!code) return false;

  const resp = await http({
    url: '/api/v1/country-blocks',
    method: 'GET',
    params: { code, blocked: 'true' },
  });

  return (resp.data || []).length > 0;
}

export function cloneExceptionsForTarget(exceptions, targetCountryCode, targetPartnerId) {
  const countryCode = targetCountryCode.trim().toUpperCase();
  const partnerId = normalizePartnerId(targetPartnerId);

  return {
    currency: (exceptions?.currency || []).map((item) => ({
      countryCode,
      currency: item.currency,
      partnerId,
    })),
    gameType: (exceptions?.gameType || []).map((item) => ({
      countryCode,
      gameType: Number(item.gameType),
      algGameType: item.algGameType !== null && item.algGameType !== undefined
        ? Number(item.algGameType)
        : null,
      partnerId,
    })),
    table: (exceptions?.table || []).map((item) => ({
      countryCode,
      tableId: Number(item.tableId),
      partnerId,
    })),
  };
}

async function postExceptionItem(http, resource, data) {
  const headers = { 'Content-Type': 'application/json' };
  try {
    await http({ url: `/api/v1/${resource}`, method: 'POST', data, headers });
    return 'created';
  } catch (err) {
    const message = err.response?.data?.message;
    if (typeof message === 'string' && message.toLowerCase().includes('already exists')) {
      return 'skipped';
    }
    throw err;
  }
}

export async function copyExceptionsToTarget(http, sourceExceptions, targetCountryCode, targetPartnerId) {
  const items = cloneExceptionsForTarget(sourceExceptions, targetCountryCode, targetPartnerId);
  const results = { created: 0, skipped: 0 };

  for (const item of items.currency) {
    const status = await postExceptionItem(http, 'country-except', item);
    results[status] += 1;
  }
  for (const item of items.gameType) {
    const status = await postExceptionItem(http, 'country-except-gametype', item);
    results[status] += 1;
  }
  for (const item of items.table) {
    const status = await postExceptionItem(http, 'country-except-table-id', item);
    results[status] += 1;
  }

  return results;
}

export function formatExceptionSummary(exceptions) {
  const currency = exceptions?.currency?.length || 0;
  const gameType = exceptions?.gameType?.length || 0;
  const table = exceptions?.table?.length || 0;
  const parts = [];

  if (currency) parts.push(`${currency} currency`);
  if (gameType) parts.push(`${gameType} game type`);
  if (table) parts.push(`${table} table`);

  return parts.length ? parts.join(', ') : 'No exceptions';
}

function rowKey(countryCode, partnerId) {
  return `${countryCode}|${normalizePartnerId(partnerId) || ''}`;
}

export function groupExceptionsToRows(currency, gameType, table) {
  const rows = new Map();

  const ensureRow = (countryCode, partnerId) => {
    const key = rowKey(countryCode, partnerId);
    if (!rows.has(key)) {
      rows.set(key, {
        countryCode,
        partnerId: normalizePartnerId(partnerId),
        exceptionsRaw: { currency: [], gameType: [], table: [] },
      });
    }
    return rows.get(key);
  };

  (currency || []).forEach((item) => {
    ensureRow(item.countryCode, item.partnerId).exceptionsRaw.currency.push(item);
  });
  (gameType || []).forEach((item) => {
    ensureRow(item.countryCode, item.partnerId).exceptionsRaw.gameType.push(item);
  });
  (table || []).forEach((item) => {
    ensureRow(item.countryCode, item.partnerId).exceptionsRaw.table.push(item);
  });

  return Array.from(rows.values());
}

export async function fetchAllExceptionRows(http, filters = {}) {
  const request = (config) => http(config);
  const params = {};
  if (filters.countryCode) {
    params.countryCode = filters.countryCode.trim().toUpperCase();
  }

  const [cur, gt, tbl] = await Promise.all([
    request({ url: '/api/v1/country-except', method: 'GET', params }),
    request({ url: '/api/v1/country-except-gametype', method: 'GET', params }),
    request({ url: '/api/v1/country-except-table-id', method: 'GET', params }),
  ]);

  let rows = groupExceptionsToRows(cur.data, gt.data, tbl.data);

  if (filters.partnerIds?.length) {
    const allowed = new Set(filters.partnerIds.map(id => normalizePartnerId(id)));
    rows = rows.filter(row => row.partnerId && allowed.has(row.partnerId));
  }

  return rows;
}
